import { motion } from "framer-motion";
import { Flower2, Gift, Home, Stamp } from "lucide-react";
import { Eyebrow, Reveal, EASE } from "@/components/site/ui";

const USES = [
  { icon: Flower2, title: "Pllaka përkujtimore", text: "Portreti i një njeriu të dashur, i gdhendur në granit për varre dhe përmendore." },
  { icon: Gift, title: "Dhurata familjare", text: "Një foto çifti, fëmijësh ose familjeje, kthyer në një dhuratë që nuk zbehet." },
  { icon: Home, title: "Dekor për shtëpinë", text: "Pllaka për mure, rafte dhe hyrje, në formën dhe madhësinë që dëshironi." },
  { icon: Stamp, title: "Logo dhe mbishkrime", text: "Emra, data, tekste dhe logo biznesi të gdhendura me saktësi." },
];

const STEPS = [
  { n: "01", title: "Dërgoni foton", text: "Na dërgoni në WhatsApp një foto të qartë, me dritë të mirë. Mund të përdoren edhe foto të vjetra." },
  { n: "02", title: "Përgatitja", text: "E përgatisim foton për gdhendje dhe bashkë zgjedhim gurin, formën dhe madhësinë e pllakës." },
  { n: "03", title: "Gdhendja me lazer", text: "Lazeri e gdhend portretin pikë pas pike në sipërfaqen e lëmuar të gurit." },
  { n: "04", title: "Dorëzimi", text: "Pllaka pastrohet, kontrollohet dhe ju dorëzohet gati për t'u vendosur." },
];

export const Perdorimet = () => (
  <section className="bg-[#EFEAE0] text-[#1B1A17] py-24 sm:py-36" data-testid="portrete-uses">
    <div className="max-w-[1400px] mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-14 items-center">
      <Reveal className="lg:col-span-5">
        <div className="relative aspect-square overflow-hidden" style={{ clipPath: "polygon(0 0, 88% 0, 100% 12%, 100% 100%, 0 100%)" }}>
          <img src="/img/laser-gift.jpg" alt="Pllakë graniti me portret çifti si dhuratë" className="w-full h-full object-cover" />
        </div>
      </Reveal>
      <div className="lg:col-span-7">
        <Reveal>
          <Eyebrow light>Përdorimet</Eyebrow>
          <h2 className="font-display font-light text-5xl sm:text-6xl mt-6 leading-[1]">Një portret, <em className="text-[#8A6A2E]">shumë kuptime</em>.</h2>
        </Reveal>
        <div className="mt-12 grid sm:grid-cols-2 gap-x-8 gap-y-10">
          {USES.map(({ icon: Icon, title, text }, i) => (
            <Reveal key={title} delay={i * 0.08}>
              <div className="border-t border-[#1B1A17]/25 pt-5" data-testid={`portrete-use-${i}`}>
                <Icon className="w-5 h-5 text-[#8A6A2E]" />
                <h3 className="font-display text-3xl mt-3">{title}</h3>
                <p className="mt-2 text-[#4A453D] leading-relaxed">{text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </div>
  </section>
);

export const SiFunksionon = () => (
  <section id="si-funksionon" className="relative bg-[#111113] py-24 sm:py-36 overflow-hidden" data-testid="portrete-steps">
    <div className="relative max-w-[1400px] mx-auto px-5 sm:px-8">
      <div className="grid lg:grid-cols-12 gap-10 items-end mb-16">
        <Reveal className="lg:col-span-7">
          <Eyebrow>Si funksionon</Eyebrow>
          <h2 className="font-display font-light text-5xl sm:text-6xl lg:text-7xl text-[#F3EEE4] mt-6 leading-[1]">Nga fotografia <em className="text-gold-grad">te guri</em>.</h2>
        </Reveal>
        <Reveal delay={0.1} className="lg:col-span-5">
          <div className="relative aspect-[3/2] overflow-hidden laser-frame">
            <img src="/img/laser-machine.jpg" alt="Makineri lazer duke gdhendur portret në granit" className="w-full h-full object-cover" />
          </div>
        </Reveal>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
        {STEPS.map((s, i) => (
          <div key={s.n} className="relative pt-8" data-testid={`portrete-step-${s.n}`}>
            <motion.span className="absolute top-0 left-0 right-0 h-px bg-[#C9A55C]/60 origin-left" initial={{ scaleX: 0 }} whileInView={{ scaleX: 1 }} viewport={{ once: true, amount: 0.1 }} transition={{ duration: 1.2, delay: i * 0.12, ease: EASE }} />
            <Reveal delay={i * 0.1}>
              <span className="font-display italic text-6xl text-[#C9A55C] leading-none">{s.n}</span>
              <h3 className="font-display text-3xl text-[#F3EEE4] mt-4">{s.title}</h3>
              <p className="mt-3 text-[#CFC8BA] leading-relaxed">{s.text}</p>
            </Reveal>
          </div>
        ))}
      </div>
    </div>
  </section>
);

export const Detaji = () => (
  <section className="relative bg-[#0B0B0C] overflow-hidden" data-testid="portrete-detail">
    <div className="relative h-[70vh] sm:h-[85vh]">
      <img src="/img/laser-detail.jpg" alt="Detaj i gdhendjes me lazer në granit" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0B0B0C] via-[#0B0B0C]/30 to-transparent" />
      <div className="absolute bottom-0 inset-x-0 max-w-[1400px] mx-auto px-5 sm:px-8 pb-14 sm:pb-20">
        <Reveal>
          <h2 className="font-display font-light text-5xl sm:text-7xl lg:text-8xl text-[#F3EEE4] leading-[0.95] max-w-4xl">Çdo detaj, <em className="text-gold-grad">pikë pas pike</em>.</h2>
          <p className="mt-6 text-[#DDD6C9] max-w-lg leading-relaxed">Graniti i zi i lëmuar jep kontrastin më të fortë për portretet. Forma dhe madhësia e pllakës bëhen me porosi.</p>
        </Reveal>
      </div>
    </div>
  </section>
);
